/* eslint-disable react/display-name */
import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { utilities } from '@weatherbug/common';
import { reduxConnect } from '../../../common';
import { translate } from '../../../common/translation/translate';
import { WeatherDetailsListItem,
    WeatherDetailsCellContainerLeft,
    WeatherDetailsCellContainerRight,
    WeatherDetailsCellLabel,
    WeatherDetailsCellValue } from '../weatherDetailsStyledComponents';
import { PressureArrowIndicator } from '../pressureChangeArrow';
import { isValidDisplayValue } from './formatObsData';
import { getWindDirection } from '../../../common/utilities/unitUtils';

const { units } = utilities;
const defaultObsValue = '--';
const pressureArrowDirections = {
    none: -1,
    up: 0,
    down: 180
};

const mapStoreStateToProps = storeState => {
    const { selectedStationObservations, units: storeStateUnits } =  storeState ?? {};
    const appState = storeState.appState.appState || {};
    const locality = appState?.locality || {};
    const latitude = locality?.Latitude;
    const longitude = locality?.Longitude;
    const { pasl: pressure, wg: windGust, wgd: windGustDirectionDaily } = selectedStationObservations?.data?.wsobs?.so ?? {};
    const { windUnit, pressureUnit } = storeStateUnits ?? {};

    return {
        windGust,
        windGustDirectionDaily,
        pressure,
        latitude,
        longitude,
        windUnit,
        pressureUnit
    };
};

const mapDispatchToProps = {

};

export const PressureAndWindGustView = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({ pressure, windGust, pressureUnit, latitude, longitude, windUnit, windGustDirectionDaily }) => {
    const [ prevPressure, setPrevPressure ] = useState();
    const [ prevPressureUnit, setPrevPressureUnit ] = useState();
    const [ pressureArrowDirection, setPressureArrowDirection ] = useState(pressureArrowDirections.none);

    const windSpeedUnitAbbrev = units.speedUnitsEnum[windUnit];
    const pressureUnitAbbrev = units.pressureUnitAbbreviations[pressureUnit];

    const windGustDirectionLabelDisplay = windGustDirectionDaily >= 0 && isValidDisplayValue(windGustDirectionDaily) ? getWindDirection(windGustDirectionDaily) : '';
    const displayWindGustDaily = windGust >= 0 && isValidDisplayValue(windGust) ? `${windGustDirectionLabelDisplay} ${windGust} ${windSpeedUnitAbbrev}` : defaultObsValue;

    const displayPressure = pressure >= 0 && isValidDisplayValue(pressure) ? `${pressure}${pressureUnitAbbrev}` : defaultObsValue;

    useEffect(() => {

        if (pressure !== undefined && pressureUnit !== undefined) {
            setPrevPressure(pressure);
            setPrevPressureUnit(pressureUnit);
        }

        if (pressure !== undefined && pressure !== prevPressure && pressureUnit !== undefined && pressureUnit === prevPressureUnit) {
            const pressureDiff = prevPressure ? pressure - prevPressure : null;
            const ignoreMinorPressureChanges = pressureDiff && (pressureDiff <= 0.005 && pressureDiff >= -0.005);
            if (!ignoreMinorPressureChanges) {
                if (pressure > prevPressure) {
                    setPressureArrowDirection(pressureArrowDirections.up);
                } else if(pressure < prevPressure) {
                    setPressureArrowDirection(pressureArrowDirections.down);
                }else{
                    setPressureArrowDirection(pressureArrowDirections.none);
                }
                setPrevPressure(pressure);
            }
        }else if(pressure !== undefined && pressure === prevPressure) {
            setPressureArrowDirection(pressureArrowDirections.none);
        }
    }, [pressure]);

    useEffect(()=> {
        setPrevPressure(pressure);
        setPressureArrowDirection(pressureArrowDirections.none);
    }, [latitude, longitude, pressureUnit]);

    return (
        <WeatherDetailsListItem key='pressure-wind.gust'>
            <WeatherDetailsCellContainerLeft>
                <WeatherDetailsCellLabel>{translate('pressure')}</WeatherDetailsCellLabel>
                <WeatherDetailsCellValue>{displayPressure}</WeatherDetailsCellValue>
                { displayPressure !== defaultObsValue && pressureArrowDirection !== pressureArrowDirections.none && <PressureArrowIndicator pressureArrowDirection={pressureArrowDirection} /> }
            </WeatherDetailsCellContainerLeft>
            <WeatherDetailsCellContainerRight>
                <WeatherDetailsCellLabel>{translate('windGust')}</WeatherDetailsCellLabel>
                <WeatherDetailsCellValue>{displayWindGustDaily}</WeatherDetailsCellValue>
            </WeatherDetailsCellContainerRight>
        </WeatherDetailsListItem>
    );
}));

PressureAndWindGustView.propTypes = {
    pressure: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    windGust: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    pressureUnit: PropTypes.number,
    windUnit: PropTypes.number,
    windGustDirectionDaily: PropTypes.string
};

PressureAndWindGustView.displayName = 'PressureAndWindGustView';
